import { CreditCard, ArrowRight, Crown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import PageTitleScript from "@/components/MetaScript";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { proFeatures, cancellationAlertMessage } from "@/config/subscription";
import { planApi } from "@/api/planApi";
import { LoadingModal } from "@/components/ui/LoadingModal";
import { useToastHandler } from "@/hooks/useToastHandler";
import { useUserInfo } from "@/hooks/useUserInfo";

export function SettingsSubscriptionProView() {
  const navigate = useNavigate();
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { handleError } = useToastHandler();

  const { data: userInfo } = useUserInfo();

  const handleManageBilling = async () => {
    try {
      setIsLoading(true);
      const response = await planApi.getBillingPortal();

      // Redirect to billing portal
      window.location.href = response.portal_url;
    } catch (error) {
      handleError(error);
      setIsLoading(false);
    } finally {
      // Don't set loading to false here since we're redirecting
    }
  };

  const handleCancelSubscription = () => {
    navigate("/settings/subscription/cancel");
  };

  return (
    <div className="flex flex-col">
      <div className="bg-gradient-to-r from-gray-900 to-black rounded-xl text-accent-fg-hot shadow-lg mb-10">
        <PageTitleScript title="Pro Access" />

        <div className="container mx-auto px-8 py-10 max-w-2xl">
          {/* Header */}

          <div className="flex items-center gap-3 mb-8">
            <div className=" w-8 h-8 bg-accent-bg-hot rounded-full flex items-center justify-center">
              <Crown className="w-5 h-5 text-accent-fg-hot" />
            </div>
            <h1 className="text-3xl font-bold text-accent-fg-hot">
              PRO Access
            </h1>
          </div>

          {/* Pro Features Active */}

          <div className="space-y-4 mb-12">
            {proFeatures.map((feature, index) => (
              <div key={index} className="flex flex-row items-center gap-1">
                <div className="w-6">
                  <svg
                    className="w-4 h-4 text-accent-fg-hot"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"
                    ></path>
                  </svg>
                </div>
                <p
                  className="flex-1 text-base md:text-lg leading-tight"
                  dangerouslySetInnerHTML={{ __html: feature }}
                ></p>
              </div>
            ))}
          </div>

          {/* Current Plan */}
          <div className="bg-slate-800/50 backdrop-blur rounded-2xl p-6  border border-slate-700">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h4 className="text-sm font-medium text-accent-fg-hot/50">
                  Current Plan
                </h4>
                <p className="text-tx-success font-bold text-xl">
                  Pro{" "}
                  {userInfo.subscription.plan_cycle == "monthly"
                    ? "Monthly"
                    : "Yearly"}
                </p>
              </div>
              <div className="text-right">
                <h4 className="text-sm font-medium text-accent-fg-hot/50">
                  {userInfo.subscription.plan_cycle}
                </h4>
                <p className="text-accent-fg-hot font-bold text-xl">
                  ${userInfo.subscription.plan_price}
                </p>
              </div>
            </div>

            {/* <div className="flex items-center gap-2 mb-6">
            <Calendar className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-300">
              Next billing: January 15, 2025
            </span>
          </div> */}

            <Button
              variant="outline"
              onClick={handleManageBilling}
              className="w-full bg-transparent border-gray-600 text-accent-fg-hot hover:bg-slate-700 hover:border-gray-500"
            >
              <CreditCard className="h-4 w-4 mr-2" />
              Manage Billing
            </Button>
          </div>
        </div>
      </div>

      {/* Cancel Subscription */}
      <div>
        <Button
          variant="ghost"
          onClick={() => setShowCancelDialog(true)}
          className="w-full bg-transparent text-tx-muted underline underline-offset-4"
        >
          Cancel Subscription
        </Button>
      </div>

      {/* Loading Modal */}
      <LoadingModal
        isOpen={isLoading}
        message="Opening billing portal..."
        subMessage="Redirecting you to manage your subscription..."
      />

      {/* Cancel Confirmation Dialog */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent 
          className="p-6 border-0 overflow-hidden max-w-md bg-white"
          aria-describedby="cancel-description"
        >
          <DialogTitle className="text-2xl font-bold">Cancel Pro?</DialogTitle>
          <div className="border-t border-gray-200"></div>
          <div id="cancel-description" className="text-tx-default text-base mb-5 space-y-2">
            {cancellationAlertMessage.map((point, index) => (
              <div key={index} className="flex items-start gap-2">
                <div className="w-1.5 h-1.5 bg-gray-400 rounded-full mt-2 flex-shrink-0" />
                <p dangerouslySetInnerHTML={{ __html: point }} />
              </div>
            ))}
          </div>

          <Button
            variant="destructive"
            size="lg"
            className="w-full"
            onClick={handleCancelSubscription}
          >
            Continue to cancel
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>

          <p className="text-xs text-tx-muted text-center leading-tight">
            Your plan is not cancelled until you click "Confirm Cancellation" on
            the next page.
          </p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
